import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  PointOfSale,
  TrendingUp,
  Warning,
  CheckCircle,
  Sync,
  Settings,
  Receipt,
  AttachMoney,
} from '@mui/icons-material';
import { api } from '../config/API_URL';
import { motion } from 'framer-motion';

export default function SalesDashboard() {
  const [salesData, setSalesData] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showPOSSettings, setShowPOSSettings] = useState(false);
  const [posConfig, setPosConfig] = useState({
    pos_system: 'dutchie',
    api_key: '',
    location_id: ''
  });

  useEffect(() => {
    loadSalesData();
  }, [selectedDate]);

  const loadSalesData = async () => {
    try {
      const [salesRes, reconRes] = await Promise.all([
        api.get(`/metrc/sales/daily?date=${selectedDate}`),
        api.get(`/metrc/sales/reconciliation?date=${selectedDate}`)
      ]);
      setSalesData(salesRes.data);
      setReconciliation(reconRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load sales:', err);
      setLoading(false);
    }
  };

  const syncPOS = async () => {
    setSyncing(true);
    try {
      await api.post('/metrc/sales/sync-pos', { date: selectedDate });
      await loadSalesData();
      alert('✅ POS sync complete!');
    } catch (err) {
      alert('❌ Sync failed: ' + err.response?.data?.error);
    }
    setSyncing(false);
  };

  const savePOSConfig = async () => {
    try {
      await api.post('/metrc/org/pos-connect', posConfig);
      setShowPOSSettings(false);
      alert('✅ POS connected!');
    } catch (err) {
      alert('❌ Connection failed');
    }
  };

  const markReconciled = async () => {
    try {
      await api.post('/metrc/sales/mark-reconciled', { date: selectedDate });
      await loadSalesData();
      alert('✅ Sales reconciled for ' + selectedDate);
    } catch (err) {
      alert('❌ Failed to mark reconciled');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const variance = reconciliation?.variance || 0;
  const hasVariance = Math.abs(variance) > 0.01;

  return (
    <Box>
      {/* Header with Date Picker */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Daily Sales Report
              </Typography>
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="small"
                sx={{ width: 200 }}
              />
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={() => setShowPOSSettings(true)}
              >
                POS Settings
              </Button>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
                onClick={syncPOS}
                disabled={syncing}
              >
                {syncing ? 'Syncing...' : 'Sync POS'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Sales Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Total Sales
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      ${salesData?.total_sales?.toLocaleString() || '0.00'}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Transactions
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {salesData?.transaction_count || 0}
                    </Typography>
                  </Box>
                  <Receipt sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Avg Transaction
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      ${salesData?.avg_transaction?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <Card sx={{
              background: hasVariance
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
            }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Variance
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: hasVariance ? 'error.main' : 'success.main'
                      }}
                    >
                      ${Math.abs(variance).toFixed(2)}
                    </Typography>
                  </Box>
                  {hasVariance ? (
                    <Warning sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
                  ) : (
                    <CheckCircle sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Reconciliation Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Reconciliation Status
          </Typography>

          {reconciliation?.is_reconciled ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Reconciled</strong> • Completed at {new Date(reconciliation.reconciled_at).toLocaleString()}
              {reconciliation.reconciled_by && ` by ${reconciliation.reconciled_by}`}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Not Reconciled</strong> • Please review and reconcile sales for this date
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  POS Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  ${reconciliation?.pos_total?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Metrc Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  ${reconciliation?.metrc_total?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Variance
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: hasVariance ? 'error.main' : 'success.main'
                  }}
                >
                  ${variance.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {!reconciliation?.is_reconciled && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={markReconciled}
                disabled={hasVariance}
                fullWidth
              >
                {hasVariance ? 'Resolve Variance First' : 'Mark as Reconciled'}
              </Button>
              {hasVariance && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  Cannot reconcile with variance present. Please investigate discrepancy.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Transaction Details */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Recent Transactions
          </Typography>

          {salesData?.transactions?.length === 0 ? (
            <Alert severity="info">
              No transactions found for {selectedDate}
            </Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesData?.transactions?.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell>{new Date(txn.timestamp).toLocaleTimeString()}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {txn.transaction_id}
                    </TableCell>
                    <TableCell>{txn.customer_name || 'Guest'}</TableCell>
                    <TableCell>{txn.item_count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ${txn.total.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip label={txn.payment_method} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={txn.status}
                        size="small"
                        color={txn.status === 'completed' ? 'success' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* POS Settings Dialog */}
      <Dialog open={showPOSSettings} onClose={() => setShowPOSSettings(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure POS Integration</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              select
              label="POS System"
              value={posConfig.pos_system}
              onChange={(e) => setPosConfig({ ...posConfig, pos_system: e.target.value })}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="dutchie">Dutchie</option>
              <option value="treez">Treez</option>
              <option value="flowhub">Flowhub</option>
              <option value="cova">Cova</option>
              <option value="greenline">Greenline</option>
            </TextField>
            <TextField
              label="API Key"
              value={posConfig.api_key}
              onChange={(e) => setPosConfig({ ...posConfig, api_key: e.target.value })}
              type="password"
              fullWidth
            />
            <TextField
              label="Location ID"
              value={posConfig.location_id}
              onChange={(e) => setPosConfig({ ...posConfig, location_id: e.target.value })}
              fullWidth
            />
            <Alert severity="info">
              Get your API credentials from your POS system's settings or contact support.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPOSSettings(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={savePOSConfig}
            disabled={!posConfig.api_key || !posConfig.location_id}
          >
            Connect POS
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}